"use client";

import React, { useState, useEffect } from 'react';
import { getCookie, setCookie } from 'cookies-next';
import Link from 'next/link';

/**
 * GDPR/CCPA Consent Manager Component
 * Provides cookie consent banner and privacy preference management
 * Optimized for high visibility and encouraging user acceptance
 */
export const ConsentManager = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showCompactModule, setShowCompactModule] = useState(false);
  const [consents, setConsents] = useState({
    necessary: true,
    analytics: true,
    marketing: true,
    preferences: true
  });

  useEffect(() => {
    const consentCookie = getCookie('perxify_consent');
    if (!consentCookie) {
      // No cookie: show main pop-up
      setTimeout(() => {
        setShowBanner(true);
      }, 1500);
      setShowCompactModule(false);
    } else {
      // Cookie exists: show compact module
      setShowCompactModule(true);
      try {
        const savedConsents = JSON.parse(consentCookie);
        setConsents(prevConsents => ({
          ...prevConsents,
          ...savedConsents
        }));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_e) {
        setShowBanner(true);
        setShowCompactModule(false);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsents = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setConsents(allConsents);
    saveConsents(allConsents);
    setShowBanner(false);
    setShowCompactModule(true);
  };

  const handleRejectNonEssential = () => {
    const minimalConsents = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    setConsents(minimalConsents);
    saveConsents(minimalConsents);
    setShowBanner(false);
    setShowCompactModule(true);
  };

  const handleCustomize = () => {
    setShowPreferences(true);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    saveConsents(consents);
    setShowPreferences(false);
    setShowCompactModule(true);
  };

  type ConsentType = keyof typeof consents;
  const handleConsentChange = (type: ConsentType) => {
    setConsents(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const saveConsents = (consentData: typeof consents) => {
    setCookie('perxify_consent', JSON.stringify(consentData), {
      maxAge: 60 * 60 * 24 * 180,
      path: '/'
    });
  
    if (consentData.analytics) {
      window.dispatchEvent(new Event('enableAnalytics'));
    } else {
      window.dispatchEvent(new Event('disableAnalytics'));
    }
  
    if (consentData.marketing) {
      window.dispatchEvent(new Event('enableMarketing'));
    } else {
      window.dispatchEvent(new Event('disableMarketing'));
    }
  };

  const openPreferences = () => {
    setShowCompactModule(false);
    setShowPreferences(true);
  };

  const closeCompact = () => {
    setShowCompactModule(false);
  };

  useEffect(() => {
    // No need to re-run this effect since saveConsents handles events
  }, [consents.analytics, consents.marketing]);

    // Custom scroll restoration handling for this component
  useEffect(() => {
    if (showCompactModule || showBanner || showPreferences) {
      // Disable Next.js scroll restoration when modals are open
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
    } else {
      // Re-enable when closed
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    }

    return () => {
      // Cleanup: restore default behavior
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    };
  }, [showCompactModule, showBanner, showPreferences]);

  return (
    <>
      {/* Compact Cookie Module */}
      {showCompactModule && (
        <div 
          className="fixed-notification sticky bottom-4 right-4 left-4 max-[419px]:w-auto max-[419px]:mx-2 z-20 bg-white shadow-lg rounded-lg p-4 max-w-sm w-full animate-fade-in-up"
          role="dialog"
          aria-labelledby="compact-module-title"
          aria-describedby="compact-module-text"
          style={{
            // Prevent scroll interference
            overscrollBehavior: 'contain',
            isolation: 'isolate',
            // Use transform instead of position for better performance
            transform: 'translateZ(0)',
            willChange: 'transform'
          }}
        >
          <h3 id="compact-module-title" className="text-lg font-semibold text-[#1A365D] mb-2">
            Perxify is using cookies
          </h3>
          <p id="compact-module-text" className="text-sm text-gray-700 mb-4">
            We use cookies to ensure optimal platform performance, enhance functionality, and gather insights that help us deliver better commerce experiences.
          </p>
          <div className="flex gap-3 max-[269px]:flex-col max-[269px]:gap-2">
            <button
              onClick={closeCompact}
              className="btn-cta bg-vibrant-teal hover:bg-teal-600 text-white py-2 px-4 rounded text-sm font-medium"
              aria-label="Accept and close"
            >
              OK
            </button>
            <button
              onClick={openPreferences}
              className="btn-secondary bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded text-sm"
              aria-label="Open cookie settings"
            >
              Cookie Preferences
            </button>
          </div>
        </div>
      )}

      {showBanner && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center"
          style={{
            overscrollBehavior: 'contain',
            isolation: 'isolate'
          }}
        >
          <div className="bg-white shadow-lg w-full sm:max-w-2xl sm:rounded-lg overflow-hidden animate-fade-in-up">
            <div className="p-6 border-l-4 border-bg-vibrant-teal">
              <div className="flex flex-col gap-4">
                <div>
                  <h3 className="text-xl font-bold text-[#1A365D] mb-2">We Value Your Experience</h3>
                  <p className="text-gray-700">
                    We use cookies to deliver intelligent personalization and optimize your experience on our commerce platform.
                  </p>  
                  <p className="text-gray-700 max-[240px]:hidden">
                    This helps us provide you with the best possible experience on our website.
                  </p>
                </div>
                
                <div className="bg-blue-50 p-2 rounded-lg border border-blue-100 max-[320px]:hidden">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">Why accept cookies?</span> Accepting cookies ensures you get the most out of our website with personalized content and seamless navigation.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 mt-2">
                  <button 
                    onClick={handleAcceptAll}
                    className="btn-cta py-3 px-6 text-base font-medium flex-1 order-1 sm:order-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
                  >
                    Accept All Cookies
                  </button>
                  <button 
                    onClick={handleCustomize}
                    className="btn-secondary bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 text-sm order-2"
                  >
                    Customize Settings
                  </button>
                  <button 
                    onClick={handleRejectNonEssential}
                    className="text-gray-500 hover:text-gray-700 text-sm underline order-3 sm:order-1"
                  >
                    Necessary Only
                  </button>
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  By clicking "Accept All Cookies", you agree to the storing of cookies on your device to enhance site navigation, analyze site usage, and assist in our marketing efforts. For more information, please read our <Link href="/privacy-policy" className="text-bg-vibrant-teal hover:underline">Privacy Policy</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPreferences && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          style={{
            overscrollBehavior: 'contain',
            isolation: 'isolate'
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fade-in-up">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h3 className="text-xl font-bold text-[#1A365D]">Cookie Preferences</h3>
                <button 
                  onClick={() => setShowPreferences(false)}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <div className="space-y-1">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-2">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">Recommended:</span> For the best experience, we recommend accepting all cookie categories.
                  </p>
                </div>
                
                <div className="border-b pb-2">
                  <div className="max-[684px]:gap-2 flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium">Strictly Necessary Cookies</h4>
                      <br />
                      <p className="text-sm text-gray-600">Technical cookies don't collect any information about you, that could be used for marketing purposes or to remember places you visited in the Internet.</p>
                    </div>
                    <div className="relative">
                      <input 
                        type="checkbox" 
                        checked={consents.necessary} 
                        disabled={true}
                        className="sr-only" 
                        id="necessary-cookies" 
                      />
                      <label 
                        htmlFor="necessary-cookies"
                        className="block w-10 h-6 bg-vibrant-teal rounded-full cursor-not-allowed"
                      >
                        <span className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform translate-x-4"></span>
                      </label>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">Always active</p>
                </div>

                <div className="border-b pb-4">
                  <div className="max-[684px]:gap-2 flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium">Analytics Cookies</h4>
                      <br />
                      <p className="text-sm text-gray-600">Analytics cookies help us understand user behavior and platform performance, enabling us to optimize the commerce experience and measure engagement effectiveness while protecting your privacy.</p>
                    </div>
                    <div className="relative">
                      <input 
                        type="checkbox" 
                        checked={consents.analytics} 
                        onChange={() => handleConsentChange('analytics')}
                        className="sr-only" 
                        id="analytics-cookies" 
                      />
                      <label 
                        htmlFor="analytics-cookies"
                        className={`block w-10 h-6 rounded-full cursor-pointer ${consents.analytics ? 'bg-vibrant-teal' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform ${consents.analytics ? 'translate-x-4' : ''}`}></span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="border-b pb-4">
                  <div className="max-[684px]:gap-2 flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium">Marketing Cookies</h4>
                      <br />
                      <p className="text-sm text-gray-600">These cookies help us show relevant advertisements and measure their effectiveness.</p>
                    </div>
                    <div className="relative">
                      <input 
                        type="checkbox" 
                        checked={consents.marketing} 
                        onChange={() => handleConsentChange('marketing')}
                        className="sr-only" 
                        id="marketing-cookies" 
                      />
                      <label 
                        htmlFor="marketing-cookies"
                        className={`block w-10 h-6 rounded-full cursor-pointer ${consents.marketing ? 'bg-vibrant-teal' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform ${consents.marketing ? 'translate-x-4' : ''}`}></span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="border-b pb-4">
                  <div className="max-[684px]:gap-2 flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium">Preferences Cookies</h4>
                      <br />
                      <p className="text-sm text-gray-600">These cookies remember your settings and preferences to enhance your experience.</p>
                    </div>
                    <div className="relative">
                      <input 
                        type="checkbox" 
                        checked={consents.preferences} 
                        onChange={() => handleConsentChange('preferences')}
                        className="sr-only" 
                        id="preferences-cookies" 
                      />
                      <label 
                        htmlFor="preferences-cookies"
                        className={`block w-10 h-6 rounded-full cursor-pointer ${consents.preferences ? 'bg-vibrant-teal' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform ${consents.preferences ? 'translate-x-4' : ''}`}></span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3 max-[379px]:flex-col max-[379px]:gap-2">
                  <button 
                    onClick={() => setShowPreferences(false)}
                    className="btn-secondary bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleSavePreferences}
                    className="btn-cta py-2 px-4"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Simple Cookie Banner Component
 * A lightweight cookie consent banner for performance-optimized contexts
 */
interface SimpleCookieBannerProps {
  onAccept?: () => void;
  privacyPolicyUrl?: string;
}

export const SimpleCookieBanner = ({ onAccept, privacyPolicyUrl = '/privacy-policy' }: SimpleCookieBannerProps) => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consentCookie = getCookie('perxify_consent');
    if (!consentCookie) {
      setTimeout(() => {
        setShowBanner(true);
      }, 1500);
    }
  }, []);

  const handleAccept = () => {
    const allConsents = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setCookie('perxify_consent', JSON.stringify(allConsents), {
      maxAge: 60 * 60 * 24 * 180,
      path: '/'
    });
    if (allConsents.analytics) {
      window.dispatchEvent(new Event('enableAnalytics'));
    }
    if (allConsents.marketing) {
      window.dispatchEvent(new Event('enableMarketing'));
    }
    setShowBanner(false);
    if (onAccept) onAccept();
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed-notification sticky bottom-4 left-4 right-4 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-md mx-auto"
      style={{
        overscrollBehavior: 'contain',
        isolation: 'isolate'
      }}
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-700">
          We use cookies to improve your experience. By continuing to use our site, you accept our use of cookies.
        </p>
        <div className="flex gap-2">
          <button
            onClick={handleAccept}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Accept
          </button>
          <Link
            href={privacyPolicyUrl}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Learn More
          </Link>
        </div>
      </div>
    </div>
  );
};

/**
 * Privacy Policy Link Component
 * Adds a link to the privacy policy page
 */
export const PrivacyPolicyLink = ({ className = "" }: { className?: string }) => {
  return (
    <Link 
      href="/privacy-policy" 
      className={`text-sm hover:underline ${className}`}
    >
      Privacy Policy
    </Link>
  );
};

/**
 * Data Processing Disclosure Component
 * Adds a disclosure about data processing for forms
 */
export const DataProcessingDisclosure = ({ className = "" }: { className?: string }) => {
  return (
    <p className={`text-xs text-gray-500 mt-2 ${className}`}>
      By submitting this form, you agree to our <Link href="/privacy-policy" className="underline">Privacy Policy</Link> and consent to Perxify processing your information as described therein. We'll handle your data with care and never sell it to third parties.
    </p>
  );
};